import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrickWall, CirclePlus, EyeOff, LayoutGrid, List, Plus, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import FallbackImage from "@/components/FallbackImage";
import { matchesName } from "../../utils/search";

export interface BrickSeriesSummary {
  _id: string;
  name: string;
  thumbnail: string | null;
  displayThumbnail: string | null;
  hidden?: boolean;
  bricks?: unknown[];
}

type Status = "visible" | "hidden";
type View = "grid" | "list";

const STATUS_LABELS: Record<Status, string> = { visible: "Zichtbaar", hidden: "Verborgen" };
// Kept from before the rename to brick series, so saved preferences still apply.
const VIEW_KEY = "brixture.collectionsView";

const statusOf = (series: BrickSeriesSummary): Status =>
  series.hidden ? "hidden" : "visible";

const brickCount = (series: BrickSeriesSummary) => series.bricks?.length ?? 0;

const initialView = (): View => {
  try {
    return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
};

const Thumbnail: React.FC<{ series: BrickSeriesSummary; className?: string }> = ({
  series,
  className,
}) =>
  (series.displayThumbnail ?? series.thumbnail) ? (
    <FallbackImage
      className={cn("bg-muted object-cover", className)}
      src={series.displayThumbnail}
      fallback={series.thumbnail}
      loading="lazy"
    />
  ) : (
    <div
      className={cn("flex items-center justify-center bg-muted text-muted-foreground", className)}
    >
      <BrickWall className="size-6" aria-hidden="true" />
    </div>
  );

const HiddenBadge = () => (
  <Badge variant="outline" className="text-muted-foreground">
    <EyeOff />
    Verborgen
  </Badge>
);

const SeriesCard: React.FC<{ series: BrickSeriesSummary; to: string }> = ({
  series,
  to,
}) => (
  <Link
    to={to}
    className="group overflow-hidden rounded-xl border bg-card shadow-xs outline-none transition-shadow hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
  >
    <Thumbnail
      series={series}
      className={cn(
        "aspect-4/3 w-full transition-transform duration-300 group-hover:scale-[1.02]",
        series.hidden && "opacity-60 grayscale-[.6]",
      )}
    />
    <div className="flex flex-col gap-2 border-t p-3">
      <span className="truncate text-sm font-medium">{series.name}</span>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary" className="tabular-nums">
          {brickCount(series)} stenen
        </Badge>
        {series.hidden && <HiddenBadge />}
      </div>
    </div>
  </Link>
);

const SeriesTable: React.FC<{
  seriesList: BrickSeriesSummary[];
  linkTo(series: BrickSeriesSummary): string;
}> = ({ seriesList, linkTo }) => {
  const navigate = useNavigate();
  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2.5 font-medium">Collectie</th>
            <th className="px-4 py-2.5 text-right font-medium">Stenen</th>
            <th className="px-4 py-2.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {seriesList.map((series) => (
            <tr
              key={series._id}
              className="cursor-pointer border-t transition-colors hover:bg-muted/50"
              onClick={() => navigate(linkTo(series))}
            >
              <td className="px-4 py-2">
                <div className="flex items-center gap-3">
                  <Thumbnail series={series} className="h-10 w-14 shrink-0 rounded-md" />
                  <Link
                    to={linkTo(series)}
                    className="font-medium outline-none hover:underline focus-visible:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {series.name}
                  </Link>
                </div>
              </td>
              <td className="px-4 py-2 text-right tabular-nums">{brickCount(series)}</td>
              <td className="px-4 py-2">
                {series.hidden ? (
                  <HiddenBadge />
                ) : (
                  <Badge variant="outline">{STATUS_LABELS.visible}</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface Props {
  organizationId: string;
  brickSeries: BrickSeriesSummary[] | null;
  onCreate(): void;
}

const OrganizationBrickSeries: React.FC<Props> = ({
  organizationId,
  brickSeries,
  onCreate,
}) => {
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [view, setView] = useState<View>(initialView);

  const changeView = (next: View) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Remembering the view is a convenience only.
    }
  };

  const toggleStatus = (status: Status, checked: boolean) =>
    setStatuses((current) =>
      checked ? [...current, status] : current.filter((s) => s !== status),
    );

  const visibleSeries = brickSeries?.filter(
    (series) =>
      matchesName(series.name, query) &&
      (statuses.length === 0 || statuses.includes(statusOf(series))),
  );
  const linkTo = (series: BrickSeriesSummary) =>
    `/${organizationId}/series/${series._id}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="series-search"
            type="search"
            placeholder="Filter op naam…"
            aria-label="Filter collecties op naam"
            className="h-8 pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="border-dashed">
              <CirclePlus />
              Status
              {statuses.length > 0 && (
                <>
                  <Separator
                    orientation="vertical"
                    className="mx-0.5 data-[orientation=vertical]:h-4"
                  />
                  {statuses.map((status) => (
                    <Badge key={status} variant="secondary" className="rounded-sm px-1 font-normal">
                      {STATUS_LABELS[status]}
                    </Badge>
                  ))}
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {(Object.keys(STATUS_LABELS) as Status[]).map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={statuses.includes(status)}
                onCheckedChange={(checked) => toggleStatus(status, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                <span className="flex-1">{STATUS_LABELS[status]}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {brickSeries?.filter((c) => statusOf(c) === status).length ?? 0}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
            {statuses.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="justify-center" onSelect={() => setStatuses([])}>
                  Filters wissen
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <div
          className="ml-auto inline-flex overflow-hidden rounded-md border shadow-xs"
          role="group"
          aria-label="Weergave"
        >
          {(
            [
              ["grid", LayoutGrid, "Raster"],
              ["list", List, "Lijst"],
            ] as const
          ).map(([value, Icon, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              aria-label={label}
              title={label}
              onClick={() => changeView(value)}
              className={cn(
                "flex size-8 items-center justify-center text-muted-foreground outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                view === value && "bg-accent text-foreground",
              )}
            >
              <Icon className="size-4" />
            </button>
          ))}
        </div>
      </div>

      {visibleSeries == null ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="overflow-hidden rounded-xl border">
              <Skeleton className="aspect-4/3 w-full rounded-none" />
              <div className="flex flex-col gap-2 p-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-5 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : brickSeries?.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-16 text-center">
          <span className="flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <BrickWall className="size-6" />
          </span>
          <div>
            <p className="font-medium">Nog geen collecties</p>
            <p className="text-sm text-muted-foreground">
              Maak een collectie aan en upload de stenen die klanten in de widget kunnen kiezen.
            </p>
          </div>
          <Button onClick={onCreate}>
            <Plus />
            Nieuwe collectie
          </Button>
        </div>
      ) : visibleSeries.length === 0 ? (
        <p className="rounded-xl border border-dashed px-6 py-12 text-center text-sm text-muted-foreground">
          Geen collecties gevonden
        </p>
      ) : view === "grid" ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4">
          {visibleSeries.map((series) => (
            <SeriesCard key={series._id} series={series} to={linkTo(series)} />
          ))}
        </div>
      ) : (
        <SeriesTable seriesList={visibleSeries} linkTo={linkTo} />
      )}
    </div>
  );
};

export default OrganizationBrickSeries;
